// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Board outlines from the Edge.Cuts layer.
 *
 * The Python engine gets these from pcbnew's `GetBoardPolygonOutlines()`, a
 * contour solver we do not have in a browser. deltaKi's display list is no help
 * either: its geometry is baked into opaque `Path2D` objects. So the contours
 * are rebuilt here from the parse tree.
 *
 * Three steps: collect every Edge.Cuts graphic (including the ones drawn inside
 * footprints, which is normal and easy to miss), flatten curves to polylines,
 * then chain the loose segments end-to-end into closed rings and work out which
 * ring sits inside which.
 *
 * Everything is in millimetres — the KiCad v6+ file format already is, and so is
 * the panel spec, so no unit conversion happens anywhere in this module.
 */

import type { Board, BoardObject } from '../model/board.ts';
import { type BBox, type MaybeBBox, bbox, fromPoints } from '../model/bbox.ts';
import { type Point, arcParams, rotate } from '../model/geometry.ts';
import { strokeWidth } from '../model/objects.ts';
import { type Node, atoms, get, getAll, getNums } from '../sexp/index.ts';

export const EDGE_LAYER = 'Edge.Cuts';

/** How far apart two endpoints may be and still count as the same point (mm). */
const JOIN_TOLERANCE = 1e-3;

/** Largest gap allowed between a flattened curve and the true curve (mm). */
const FLATTEN_TOLERANCE = 2e-3;

/** A closed contour. The repeated closing point is not stored. */
export type Ring = Point[];

/** A contour with any contours nested directly inside it. */
export interface Polygon {
  ext: Ring;
  holes: Ring[];
}

/** One flattened Edge.Cuts graphic, before chaining. */
export interface EdgeSegment {
  pts: Point[];
  /** True for shapes that are already loops: circles, rectangles, closed polys. */
  closed: boolean;
  /** The object this came from, for hit-testing back to the source. */
  objectId: string;
  /** Stroke width of the graphic, in mm. */
  width?: number;
}

export interface OutlineResult {
  polygons: Polygon[];
  warnings: string[];
  /**
   * Widest Edge.Cuts stroke on the board.
   *
   * KiKit widens the source area by `tolerance + edgeMaxWidth` before deciding
   * which items belong to a board, so reproducing that selection needs this.
   */
  maxEdgeWidth: number;
}

// --- reading the parse tree ---------------------------------------------------------

interface Frame {
  ox: number;
  oy: number;
  rot: number;
}

const IDENTITY: Frame = { ox: 0, oy: 0, rot: 0 };

function tp(f: Frame, x: number, y: number): Point {
  if (f.rot === 0) return [x + f.ox, y + f.oy];
  const [rx, ry] = rotate(x, y, f.rot);
  return [rx + f.ox, ry + f.oy];
}

function xyOf(n: Node, token: string, frame: Frame): Point | null {
  if (!get(n, token)) return null;
  const v = getNums(n, token);
  return v.length >= 2 ? tp(frame, v[0]!, v[1]!) : null;
}

function pointsOf(n: Node, frame: Frame): Point[] {
  const holder = get(n, 'pts');
  if (!holder) return [];
  const out: Point[] = [];
  for (const xy of getAll(holder, 'xy')) {
    const v = getNums(xy, [] as string[]);
    if (v.length >= 2) out.push(tp(frame, v[0]!, v[1]!));
  }
  return out;
}

/** Layer names on a graphic, expanded through the board's layer table. */
function graphicLayers(n: Node, board: Board): string[] {
  for (const token of ['layer', 'layers'] as const) {
    const holder = get(n, token);
    if (!holder) continue;
    const names = atoms(holder).map((a) => a.value);
    if (names.length > 0) return board.layerTable.expandAll(names);
  }
  return [];
}

/**
 * Sample an arc densely enough that the chord never strays further than
 * `FLATTEN_TOLERANCE` from the true curve.
 */
function flattenArc(start: Point, mid: Point, end: Point): Point[] {
  const p = arcParams(start, mid, end);
  // Collinear points are a straight line, which is what `arcParams` reports null for.
  if (p === null) return [start, end];

  let sweep = p.a1 - p.a0;
  const full = 2 * Math.PI;
  if (p.counterclockwise) {
    while (sweep > 0) sweep -= full;
    if (sweep === 0) sweep = -full;
  } else {
    while (sweep < 0) sweep += full;
    if (sweep === 0) sweep = full;
  }

  const steps = arcSteps(p.r, Math.abs(sweep));
  const out: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = p.a0 + (sweep * i) / steps;
    out.push([p.cx + p.r * Math.cos(a), p.cy + p.r * Math.sin(a)]);
  }
  // Pin the ends to the authored points so chaining sees exact coordinates.
  out[0] = start;
  out[out.length - 1] = end;
  return out;
}

function arcSteps(radius: number, sweep: number): number {
  if (!(radius > FLATTEN_TOLERANCE)) return 2;
  // Sagitta of one segment: r·(1 − cos(θ/2)). Solve for θ at the tolerance.
  const maxAngle = 2 * Math.acos(Math.max(-1, 1 - FLATTEN_TOLERANCE / radius));
  if (!Number.isFinite(maxAngle) || maxAngle <= 0) return 64;
  return Math.min(2048, Math.max(2, Math.ceil(sweep / maxAngle)));
}

function flattenCircle(center: Point, edge: Point): Point[] {
  const r = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
  const steps = arcSteps(r, 2 * Math.PI);
  const out: Point[] = [];
  // Open ring: the closing point is implied, matching the `Ring` contract.
  for (let i = 0; i < steps; i++) {
    const a = (2 * Math.PI * i) / steps;
    out.push([center[0] + r * Math.cos(a), center[1] + r * Math.sin(a)]);
  }
  return out;
}

/** Cubic Bézier, sampled at a fixed density — Edge.Cuts curves are rare and small. */
function flattenCurve(pts: Point[]): Point[] {
  if (pts.length < 4) return pts;
  const [p0, p1, p2, p3] = pts as [Point, Point, Point, Point];
  const steps = 32;
  const out: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push([
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
    ]);
  }
  return out;
}

function segmentFrom(n: Node, frame: Frame, objectId: string): EdgeSegment | null {
  const kind = n.name.replace(/^fp_/, 'gr_');
  switch (kind) {
    case 'gr_line': {
      const a = xyOf(n, 'start', frame);
      const b = xyOf(n, 'end', frame);
      return a && b ? { pts: [a, b], closed: false, objectId } : null;
    }
    case 'gr_arc': {
      const s = xyOf(n, 'start', frame);
      const m = xyOf(n, 'mid', frame);
      const e = xyOf(n, 'end', frame);
      if (!s || !e) return null;
      return { pts: m ? flattenArc(s, m, e) : [s, e], closed: false, objectId };
    }
    case 'gr_rect': {
      // Read the corners in local space so a rotated frame yields a true
      // rectangle rather than a parallelogram.
      const la = getNums(n, 'start');
      const lc = getNums(n, 'end');
      if (la.length < 2 || lc.length < 2) return null;
      const pts: Point[] = [
        tp(frame, la[0]!, la[1]!),
        tp(frame, lc[0]!, la[1]!),
        tp(frame, lc[0]!, lc[1]!),
        tp(frame, la[0]!, lc[1]!),
      ];
      return { pts, closed: true, objectId };
    }
    case 'gr_circle': {
      const c = xyOf(n, 'center', frame);
      const e = xyOf(n, 'end', frame);
      return c && e ? { pts: flattenCircle(c, e), closed: true, objectId } : null;
    }
    case 'gr_poly': {
      const pts = pointsOf(n, frame);
      return pts.length >= 3 ? { pts, closed: true, objectId } : null;
    }
    case 'gr_curve': {
      const pts = pointsOf(n, frame);
      return pts.length >= 2 ? { pts: flattenCurve(pts), closed: false, objectId } : null;
    }
    default:
      return null;
  }
}

const GRAPHIC_TOKENS = new Set([
  'gr_line', 'gr_arc', 'gr_rect', 'gr_circle', 'gr_poly', 'gr_curve',
  'fp_line', 'fp_arc', 'fp_rect', 'fp_circle', 'fp_poly', 'fp_curve',
]);

/**
 * Every Edge.Cuts graphic on the board, flattened to polylines.
 *
 * Footprints are walked as well: KiKit's own `collectEdges` chains the board's
 * drawings with every footprint's graphical items, so an outline drawn inside a
 * footprint is a supported way to author a board and must not be dropped.
 */
export function collectEdgeSegments(board: Board, layer: string = EDGE_LAYER): EdgeSegment[] {
  const out: EdgeSegment[] = [];

  const consider = (n: Node, frame: Frame, objectId: string): void => {
    if (!GRAPHIC_TOKENS.has(n.name)) return;
    if (!graphicLayers(n, board).includes(layer)) return;
    const seg = segmentFrom(n, frame, objectId);
    if (seg && seg.pts.length >= 2) out.push({ ...seg, width: strokeWidth(n) });
  };

  for (const o of board.objects as BoardObject[]) {
    if (o.type === 'footprint') {
      const at = getNums(o.node, 'at');
      const frame: Frame = { ox: at[0] ?? 0, oy: at[1] ?? 0, rot: at[2] ?? 0 };
      for (const child of o.node.children) {
        if (child.kind === 'node') consider(child, frame, o.id);
      }
    } else {
      consider(o.node, IDENTITY, o.id);
    }
  }
  return out;
}

// --- chaining -----------------------------------------------------------------------

function cell(p: Point): [number, number] {
  return [Math.round(p[0] / JOIN_TOLERANCE), Math.round(p[1] / JOIN_TOLERANCE)];
}

function key(p: Point): string {
  const [cx, cy] = cell(p);
  return `${cx},${cy}`;
}

/**
 * Bucket keys covering everything within the join tolerance of `p`.
 *
 * Two endpoints that should meet can straddle a bucket boundary — on one real
 * board a pair 15 nm apart quantised into different cells — so looking only in
 * the point's own bucket loses the join and the contour never closes.
 */
function neighbourKeys(p: Point): string[] {
  const [cx, cy] = cell(p);
  const out: string[] = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) out.push(`${cx + dx},${cy + dy}`);
  }
  return out;
}

function samePoint(a: Point, b: Point): boolean {
  return Math.abs(a[0] - b[0]) <= JOIN_TOLERANCE && Math.abs(a[1] - b[1]) <= JOIN_TOLERANCE;
}

/** Unit vector from `a` to `b`; zero when they coincide. */
function direction(a: Point, b: Point): Point {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  return len === 0 ? [0, 0] : [dx / len, dy / len];
}

/** Drop consecutive duplicates, and the closing point if the ring repeats it. */
function tidyRing(pts: Point[]): Ring {
  const out: Point[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && samePoint(last, p)) continue;
    out.push(p);
  }
  while (out.length > 1 && samePoint(out[0]!, out[out.length - 1]!)) out.pop();
  return out;
}

/**
 * Join open polylines end-to-end into closed rings.
 *
 * KiCad writes each edge as its own object with no ordering, and endpoints that
 * agree to within rounding rather than exactly, so this is a tolerance walk
 * rather than a lookup. A chain that runs out before closing is reported as a
 * warning and dropped: an unclosed outline is not a board, and guessing how to
 * close it would silently invent geometry.
 */
export function chainSegments(segments: EdgeSegment[]): { rings: Ring[]; warnings: string[] } {
  const rings: Ring[] = [];
  const warnings: string[] = [];

  for (const seg of segments) {
    if (seg.closed) {
      const ring = tidyRing(seg.pts);
      if (ring.length >= 3) rings.push(ring);
    }
  }

  const open = segments.filter((s) => !s.closed);
  const used = new Array<boolean>(open.length).fill(false);

  // Endpoint index: every open segment is reachable from both of its ends.
  const ends = new Map<string, number[]>();
  const addEnd = (p: Point, i: number): void => {
    const k = key(p);
    const list = ends.get(k);
    if (list) list.push(i);
    else ends.set(k, [i]);
  };
  open.forEach((s, i) => {
    addEnd(s.pts[0]!, i);
    addEnd(s.pts[s.pts.length - 1]!, i);
  });

  /**
   * The next segment to walk onto at `at`, having arrived along `incoming`.
   *
   * Where two contours touch at a point the junction has four ends, and taking
   * whichever comes first in file order can hop onto the wrong contour and leave
   * both unclosable. Picking the most clockwise turn instead traces one face at
   * a time, which is the standard rule for walking a planar subdivision.
   */
  const findNext = (
    at: Point,
    incoming: Point | null,
  ): { index: number; reversed: boolean } | null => {
    const candidates: Array<{ index: number; reversed: boolean; dir: Point }> = [];
    const seen = new Set<number>();

    for (const k of neighbourKeys(at)) {
      for (const i of ends.get(k) ?? []) {
        if (used[i] || seen.has(i)) continue;
        const s = open[i]!;
        const last = s.pts.length - 1;
        if (samePoint(s.pts[0]!, at)) {
          seen.add(i);
          candidates.push({ index: i, reversed: false, dir: direction(s.pts[0]!, s.pts[1]!) });
        } else if (samePoint(s.pts[last]!, at)) {
          seen.add(i);
          candidates.push({
            index: i,
            reversed: true,
            dir: direction(s.pts[last]!, s.pts[last - 1]!),
          });
        }
      }
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1 || incoming === null) {
      const c = candidates[0]!;
      return { index: c.index, reversed: c.reversed };
    }

    // Angle of each way out, measured clockwise from the way we came in.
    const back = Math.atan2(-incoming[1], -incoming[0]);
    let best = candidates[0]!;
    let bestTurn = Infinity;
    for (const c of candidates) {
      let turn = back - Math.atan2(c.dir[1], c.dir[0]);
      while (turn <= 0) turn += 2 * Math.PI;
      while (turn > 2 * Math.PI) turn -= 2 * Math.PI;
      if (turn < bestTurn) {
        bestTurn = turn;
        best = c;
      }
    }
    return { index: best.index, reversed: best.reversed };
  };

  for (let start = 0; start < open.length; start++) {
    if (used[start]) continue;
    used[start] = true;

    const chain = [...open[start]!.pts];
    const first = chain[0]!;
    let closed = false;

    for (;;) {
      const tail = chain[chain.length - 1]!;
      if (samePoint(tail, first) && chain.length > 2) {
        closed = true;
        break;
      }
      const prev = chain[chain.length - 2];
      const next = findNext(tail, prev ? direction(prev, tail) : null);
      if (next === null) break;
      used[next.index] = true;
      const pts = open[next.index]!.pts;
      const run = next.reversed ? [...pts].reverse() : pts;
      // `run[0]` duplicates the current tail.
      for (let i = 1; i < run.length; i++) chain.push(run[i]!);
    }

    const ring = tidyRing(chain);
    if (closed && ring.length >= 3) {
      rings.push(ring);
    } else {
      const [x, y] = first;
      warnings.push(
        `Edge.Cuts outline starting near (${x.toFixed(3)}, ${y.toFixed(3)}) does not close; ignored`,
      );
    }
  }

  return { rings, warnings };
}

// --- ring geometry ------------------------------------------------------------------

/** Twice the signed area. Positive and negative both occur; callers use |area|. */
export function ringArea(ring: Ring): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += a[0] * b[1] - b[0] * a[1];
  }
  return sum / 2;
}

export function ringBBox(ring: Ring): BBox {
  return fromPoints(ring) as BBox;
}

/** Even-odd point-in-polygon. Points exactly on the edge are not "inside". */
export function pointInRing(ring: Ring, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    if (a[1] > y !== b[1] > y && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

/** A point that is inside the ring, for containment tests against other rings. */
function representativePoint(ring: Ring): Point {
  // The centroid of a concave ring can land outside it, so fall back to probing
  // along a horizontal line through the ring until a point tests inside.
  let cx = 0;
  let cy = 0;
  for (const p of ring) {
    cx += p[0];
    cy += p[1];
  }
  cx /= ring.length;
  cy /= ring.length;
  if (pointInRing(ring, cx, cy)) return [cx, cy];

  const box = ringBBox(ring);
  for (let i = 1; i < 32; i++) {
    const y = box.minY + ((box.maxY - box.minY) * i) / 32;
    for (let j = 1; j < 32; j++) {
      const x = box.minX + ((box.maxX - box.minX) * j) / 32;
      if (pointInRing(ring, x, y)) return [x, y];
    }
  }
  return [cx, cy];
}

/**
 * Nest rings into polygons with holes.
 *
 * A ring's depth is the number of rings that contain it; even depth makes it an
 * exterior, odd makes it a hole of the smallest ring containing it. That is the
 * even-odd rule KiCad itself uses, so a board with a cutout that has an island
 * in it comes out right.
 */
export function nestRings(rings: Ring[]): Polygon[] {
  const meta = rings.map((ring) => ({
    ring,
    area: Math.abs(ringArea(ring)),
    box: ringBBox(ring),
    probe: representativePoint(ring),
  }));

  const containers = meta.map((m, i) =>
    meta
      .map((other, j) => ({ other, j }))
      .filter(
        ({ other, j }) =>
          i !== j &&
          other.area > m.area &&
          other.box.minX <= m.box.minX &&
          other.box.maxX >= m.box.maxX &&
          other.box.minY <= m.box.minY &&
          other.box.maxY >= m.box.maxY &&
          pointInRing(other.ring, m.probe[0], m.probe[1]),
      )
      .map(({ j }) => j),
  );

  const polygons: Polygon[] = [];
  const indexOfPolygon = new Map<number, number>();

  meta.forEach((m, i) => {
    if (containers[i]!.length % 2 === 0) {
      indexOfPolygon.set(i, polygons.length);
      polygons.push({ ext: m.ring, holes: [] });
    }
  });

  meta.forEach((m, i) => {
    if (containers[i]!.length % 2 === 0) return;
    // Immediate parent: the smallest ring that contains this one.
    let parent = -1;
    for (const j of containers[i]!) {
      if (parent === -1 || meta[j]!.area < meta[parent]!.area) parent = j;
    }
    const target = parent === -1 ? undefined : indexOfPolygon.get(parent);
    if (target !== undefined) polygons[target]!.holes.push(m.ring);
  });

  return polygons;
}

/** Every closed Edge.Cuts outline of a board, as polygons with holes. */
export function extractOutlines(board: Board, layer: string = EDGE_LAYER): OutlineResult {
  const segments = collectEdgeSegments(board, layer);
  const { rings, warnings } = chainSegments(segments);
  const maxEdgeWidth = segments.reduce((widest, s) => Math.max(widest, s.width ?? 0), 0);
  return { polygons: nestRings(rings), warnings, maxEdgeWidth };
}

export function polygonBBox(p: Polygon): BBox {
  return ringBBox(p.ext);
}

export function polygonsBBox(polys: Polygon[]): MaybeBBox {
  let out: MaybeBBox = null;
  for (const p of polys) {
    const b = polygonBBox(p);
    out = out === null ? b : bbox(
      Math.min(out.minX, b.minX),
      Math.min(out.minY, b.minY),
      Math.max(out.maxX, b.maxX),
      Math.max(out.maxY, b.maxY),
    );
  }
  return out;
}

/** Exterior first, then holes — the iteration tab ray-casting wants. */
export function ringsOf(p: Polygon): Ring[] {
  return [p.ext, ...p.holes];
}

/** Inside the polygon proper: within the exterior and not within a hole. */
export function pointInPolygon(p: Polygon, x: number, y: number): boolean {
  if (!pointInRing(p.ext, x, y)) return false;
  return !p.holes.some((h) => pointInRing(h, x, y));
}
